import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { useResultsStoryAssets } from '@sorare/core/src/components/animations/AnimatedStory/useResultsStoryAssets';
import { ShouldVerifyUserBeforeClaiming } from '@sorare/core/src/components/rewards/ShouldVerifyUserBeforeClaiming';
import { ANY_SPORT_MY_CARDS_BOXES } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { isTypeFn } from '@sorare/core/src/lib/gql';

import { LastFixtureStoryStep } from 'components/so5/LastFixtureStory/LastFixtureStoryStep';

import { Title } from '../LastFixtureStoryStep/Title';
import { useRewardsVerification } from '../RewardsRecap/useRewardsVerification';
import { CardReward } from './CardReward';
import {
  CardRewardsReveal_so5Fixture,
  CardRewardsReveal_so5Reward,
} from './__generated__/index.graphql';

const Wrapper = styled.div`
  width: 100%;
  & > * {
    width: 100%;
  }
`;

type ContentProps = {
  cardRewards: CardRewardsReveal_so5Reward[];
};
const Content = ({ cardRewards }: ContentProps) => {
  const isTabletAndAbove = useIsTabletAndAbove();

  const itemToDisplayOnDesktop = Math.min(3, cardRewards.length);
  const itemToDisplay = isTabletAndAbove ? itemToDisplayOnDesktop : 1;
  const itemWidth = `calc(${itemToDisplay} * 250px + var(--double-unit) * ${itemToDisplay - 1})`;

  return (
    <div
      style={{
        width: itemWidth,
      }}
    >
      <Scrollable itemToDisplay={itemToDisplay} withMask>
        {cardRewards.map(reward => {
          return (
            <Wrapper key={reward.slug}>
              <ShouldVerifyUserBeforeClaiming
                hasBlockchainRewards={
                  !reward.rewards
                    .filter(isTypeFn('AnyCardReward'))
                    .every(({ card }) => card.rarity === Rarity.common)
                }
              >
                {({ disabledClaim }) => (
                  <CardReward
                    key={reward.slug}
                    reward={reward}
                    disableClaim={disabledClaim}
                  />
                )}
              </ShouldVerifyUserBeforeClaiming>
            </Wrapper>
          );
        })}
      </Scrollable>
    </div>
  );
};

type Props = {
  so5Fixture?: CardRewardsReveal_so5Fixture;
  so5Rewards: CardRewardsReveal_so5Reward[];
  onClose: ComponentProps<typeof LastFixtureStoryStep>['onClose'];
};
export const CardRewardsReveal = ({
  so5Fixture,
  so5Rewards,
  onClose,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const navigate = useNavigate();
  const { tunnel } = useResultsStoryAssets();
  const { rewardsByType } = useRewardsVerification(so5Rewards);
  const cardRewards = useRef(rewardsByType.card);

  return (
    <LastFixtureStoryStep
      title={<Title type="cardReveal" so5Fixture={so5Fixture} />}
      video={{ asset: tunnel }}
      onClose={onClose}
      cta={
        rewardsByType.box.length > 0 && (
          <Button
            size="medium"
            fullWidth
            color="secondary"
            onClick={async () => {
              await onClose?.();
              navigate(generateSportContextPath(ANY_SPORT_MY_CARDS_BOXES));
            }}
          >
            <FormattedMessage
              id="CardRewardsReveal.cta.goToMyBoxes"
              defaultMessage="Go to My Boxes"
            />
          </Button>
        )
      }
    >
      <Content cardRewards={cardRewards.current} />
    </LastFixtureStoryStep>
  );
};

CardRewardsReveal.fragments = {
  so5Reward: gql`
    fragment CardRewardsReveal_so5Reward on So5Reward {
      slug
      rewards {
        id
        ... on AnyCardReward {
          id
          card {
            slug
            rarity: rarityTyped
          }
        }
      }
      ...CardReward_so5Reward
      ...useRewardsVerification_so5Reward
    }
    ${CardReward.fragments.so5Reward}
    ${useRewardsVerification.fragments.so5Reward}
  ` as TypedDocumentNode<CardRewardsReveal_so5Reward>,
  so5Fixture: gql`
    fragment CardRewardsReveal_so5Fixture on So5Fixture {
      slug
      mySo5Rewards {
        slug
      }
      ...Title_so5Fixture
    }
    ${Title.fragments.so5Fixture}
  ` as TypedDocumentNode<CardRewardsReveal_so5Fixture>,
};
